import { createClient, type Session, type User, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';
import { sessionRequests } from './auth/sessionRequests';
import { instrumentSupabaseClient } from './telemetry/instrumentSupabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY, SUPABASE_AUTH_STORAGE_KEY } from './supabaseConfig';

export { SUPABASE_URL, SUPABASE_ANON_KEY } from './supabaseConfig';

declare global {
  interface Window {
    supabase?: SupabaseClient;
    checkAuthStatus?: () => Promise<
      | { session: Session; user: User; error?: undefined }
      | { session: null; user: null; error?: undefined }
      | { error: unknown; session?: undefined; user?: undefined }
    >;
  }
}

const isBrowser = typeof window !== 'undefined';

if (import.meta.env.DEV) {
  const urlPresent = !!SUPABASE_URL;
  const keyPresent = !!SUPABASE_ANON_KEY;

  if (!urlPresent || !keyPresent) {
    const missing: string[] = [];
    if (!urlPresent) missing.push('VITE_SUPABASE_URL');
    if (!keyPresent) missing.push('VITE_SUPABASE_KEY');
    console.warn(
      `Missing Supabase configuration: ${missing.join(', ')}.\n` +
        'Copy trivia-app/.env.example to trivia-app/.env.local, set the values, and restart the dev server.'
    );
  }
}

const commonOptions = {
  auth: {
    storageKey: SUPABASE_AUTH_STORAGE_KEY,
    autoRefreshToken: isBrowser,
    persistSession: isBrowser,
    detectSessionInUrl: isBrowser,
  },
};

const client = sessionRequests.bindClient(
  createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    ...commonOptions,
    global: {
      fetch: sessionRequests.wrapFetch(
        (...args) => globalThis.fetch(...args),
        SUPABASE_URL,
        SUPABASE_ANON_KEY
      ),
    },
  })
);

// Bind request ownership before decorating the first application RPC/function.
instrumentSupabaseClient(client);
export const supabase = client;

const checkAuthStatus = async (): Promise<
  | { session: Session; user: User; error?: undefined }
  | { session: null; user: null; error?: undefined }
  | { error: unknown; session?: undefined; user?: undefined }
> => {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Session error:', sessionError);
      return { error: sessionError };
    }

    if (sessionData.session) {
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('User error:', userError);
        return { error: userError };
      }

      return { session: sessionData.session, user: userData.user };
    } else {
      return { session: null, user: null };
    }
  } catch (error) {
    console.error('Error checking auth status:', error);
    return { error };
  }
};

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.checkAuthStatus = checkAuthStatus;
  window.supabase = supabase;
}
